/**
 * User & audit log queries.
 *
 * Users: list, create, update (role + isActive), reset password.
 * Audit: paginated list with filters (project, user, entityType, action, date range).
 *
 * Both APIs are Admin-only; the UI gates these pages on the current user's
 * role and the server enforces it again on every request.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

interface PaginatedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export type UserRole = 'ADMIN' | 'ASSISTANT';

export interface UserRecord {
  id: string;
  username: string;
  displayName: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  rowVersion: number;
}

export interface UserCreateInput {
  username: string;
  displayName: string;
  email: string;
  role: UserRole;
  initialPassword: string;
}

export interface UserUpdateInput {
  displayName?: string;
  email?: string;
  role?: UserRole;
  isActive?: boolean;
  rowVersion: number;
}

export const usersListKey = ['users'] as const;

export function useUsersList() {
  return useQuery({
    queryKey: usersListKey,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<PaginatedList<UserRecord>>('/api/users?pageSize=200', {
        signal,
      });
      return res.items;
    },
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation<UserRecord, ApiError, UserCreateInput>({
    mutationFn: (body) => apiRequest<UserRecord>('/api/users', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersListKey });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation<UserRecord, ApiError, { id: string } & UserUpdateInput>({
    mutationFn: ({ id, ...body }) =>
      apiRequest<UserRecord>(`/api/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersListKey });
    },
  });
}

export function useResetPassword() {
  return useMutation<null, ApiError, { id: string; newPassword: string }>({
    mutationFn: ({ id, newPassword }) =>
      apiRequest<null>(`/api/users/${id}/reset-password`, {
        method: 'POST',
        body: { newPassword },
      }),
  });
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export type AuditEntityType =
  | 'PROJECT'
  | 'PROJECT_ITEM'
  | 'ORDER_BATCH'
  | 'PART_STAGE_DATE'
  | 'INSPECTION_EVENT'
  | 'BANK_GUARANTEE'
  | 'PARTS_APPROVAL_SUBMISSION'
  | 'PROJECT_DEPARTMENT'
  | 'PROJECT_DEPARTMENT_CONTACT'
  | 'SUPPLIER'
  | 'CLIENT'
  | 'DEPARTMENT'
  | 'USER';

export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'RESTORE'
  | 'SUBMIT_PARTS'
  | 'APPROVE_PARTS'
  | 'APPROVE_PARTS_WITH_EDITS'
  | 'REJECT_PARTS'
  | 'FINALIZE_PARTS'
  | 'CANCEL_BATCH'
  | 'REPLACE_BATCH'
  | 'CREATE_BG_INSTRUMENT'
  | 'CLOSE_PROJECT'
  | 'REOPEN_PROJECT'
  | 'ACTIVATE_MASTER'
  | 'DEACTIVATE_MASTER'
  | 'RESET_PASSWORD'
  | 'LOGIN'
  | 'LOGOUT';

export interface AuditEntry {
  id: string;
  occurredAt: string;
  userId: string;
  userDisplayName: string;
  entityType: AuditEntityType;
  entityId: string;
  projectId: string | null;
  action: AuditAction;
  fieldName: string | null;
  oldValueJson: string | null;
  newValueJson: string | null;
  /**
   * Free-form JSON-encoded metadata. Use `parseAuditMetadata` (apps/web/
   * src/lib/audit-metadata.ts) to render. The server can attach a
   * `reason` code (e.g. 'parent_part_deleted'), a `rejectionComment`,
   * or arbitrary key/value contexts here.
   *
   * Patch E6 fixes a long-standing mismatch — this used to be typed as
   * `reasonComment` and `context` which don't exist on the server
   * response, causing every non-UPDATE row to show "—" with no context.
   */
  metadataJson: string | null;
}

export type AuditSortKey = 'occurredAt' | 'action' | 'entityType' | 'userDisplayName';
export type SortDir = 'asc' | 'desc';

export interface AuditQuery {
  projectId?: string;
  userId?: string;
  entityType?: AuditEntityType;
  entityId?: string;
  action?: AuditAction;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
  // Patch 5 — sortable columns
  sortBy?: AuditSortKey;
  sortDir?: SortDir;
}

export const auditListKey = (q: AuditQuery) => ['audit', q] as const;

export function useAuditList(query: AuditQuery = {}) {
  return useQuery({
    queryKey: auditListKey(query),
    queryFn: ({ signal }) =>
      apiRequest<PaginatedList<AuditEntry>>('/api/audit-log', {
        query: {
          projectId: query.projectId,
          userId: query.userId,
          entityType: query.entityType,
          entityId: query.entityId,
          action: query.action,
          fromDate: query.fromDate,
          toDate: query.toDate,
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortDir: query.sortDir,
        },
        signal,
      }),
    placeholderData: (prev) => prev,
  });
}
